import { useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    useTheme,
} from '@mui/material';
import Autocomplete from '@/components/ui/Autocomplete/Autocomplete';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import CurrencyInput from '@/components/ui/CurrencyInput/CurrencyInput';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import {
    pricebookApi,
    type ConflictPayload,
    type PricebookRule,
    type YearRange,
} from '@/api/pricebook';
import type { CatalogEntry } from '@/api/catalog';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import type { AddRuleDialogProps, NewRuleState } from './IAddRuleDialog';
import { styles } from './styles';

const EMPTY_RULE: NewRuleState = {
    service_type: 'Keys',
    repair_type: '',
    part: '',
    position: '',
    price: '',
    year_start: '',
    year_end: '',
    open_ended: false,
    make: [],
    model: [],
    includes_future_models: false,
    special_order: false,
};

const buildRuleState = (rule: PricebookRule, catalog: CatalogEntry[]): NewRuleState => {
    const entry = catalog.find((c) => c.id === rule.service_detail_id);
    const yearStart = rule.year_range?.start;
    const yearEnd = rule.year_range?.end;
    const isOpenEnded = yearEnd != null && yearEnd >= 9999;
    const hasMake = Array.isArray(rule.make) && rule.make.length > 0;
    const hasModel = Array.isArray(rule.model) && rule.model.length > 0;
    return {
        service_type: entry?.service_type ?? 'Keys',
        repair_type: entry?.repair_type ?? '',
        part: entry?.part ?? '',
        position: entry?.position ?? '',
        price: rule.price != null ? String(rule.price) : '',
        year_start: yearStart != null && yearStart < 9999 ? String(yearStart) : '',
        year_end: !isOpenEnded && yearEnd != null ? String(yearEnd) : '',
        open_ended: isOpenEnded,
        make: hasMake ? (rule.make as string[]) : [],
        model: hasModel ? (rule.model as string[]) : [],
        // "All models for selected make(s)" is how the API surfaces future-inclusive rules.
        includes_future_models: hasMake && !hasModel,
        special_order: Boolean(rule.special_order),
    };
};

interface AxiosLikeError {
    response?: { data?: ConflictPayload & { status?: number; message?: string } };
    message?: string;
}

const isConflictError = (err: unknown): err is AxiosLikeError =>
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    (err as AxiosLikeError).response?.data?.status === 409;

const AddRuleDialog = ({
    open,
    pricebook,
    catalog,
    vehicleMakes,
    editRule,
    onClose,
    onAdded,
    onConflict,
}: AddRuleDialogProps) => {
    const theme = useTheme();
    const errorCtx = useError();
    const [rule, setRule] = useState<NewRuleState>(EMPTY_RULE);
    const [requiresQuote, setRequiresQuote] = useState(false);
    const [models, setModels] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editRule) {
            setRule(buildRuleState(editRule, catalog));
            setRequiresQuote(editRule.price == null);
        } else {
            setRule(EMPTY_RULE);
            setRequiresQuote(false);
        }
        setSubmitting(false);
    }, [open]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const res = await pricebookApi.getVehicleModels(
                    rule.make.length > 0 ? rule.make : undefined,
                );
                if (!cancelled) setModels(res.data?.payload ?? []);
            } catch {}
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [rule.make]);

    const repairTypeOptions = useMemo(() => {
        const filtered = catalog.filter((c) => c.service_type === rule.service_type);
        return Array.from(new Set(filtered.map((c) => c.repair_type).filter(Boolean))) as string[];
    }, [catalog, rule.service_type]);

    const partOptions = useMemo(() => {
        let filtered = catalog.filter((c) => c.service_type === rule.service_type);
        if (rule.repair_type) filtered = filtered.filter((c) => c.repair_type === rule.repair_type);
        return Array.from(new Set(filtered.map((c) => c.part).filter(Boolean))) as string[];
    }, [catalog, rule.service_type, rule.repair_type]);

    const positionOptions = useMemo(() => {
        let filtered = catalog.filter((c) => c.service_type === rule.service_type);
        if (rule.repair_type) filtered = filtered.filter((c) => c.repair_type === rule.repair_type);
        if (rule.part) filtered = filtered.filter((c) => c.part === rule.part);
        return Array.from(new Set(filtered.map((c) => c.position).filter(Boolean))) as string[];
    }, [catalog, rule.service_type, rule.repair_type, rule.part]);

    const startYear = rule.year_start ? parseInt(rule.year_start, 10) : undefined;
    const endYear = rule.open_ended ? 9999 : rule.year_end ? parseInt(rule.year_end, 10) : undefined;
    const yearRangeInvalid =
        Boolean(startYear && endYear && endYear < 9999 && endYear < startYear);

    const findServiceDetailId = (): number | null => {
        const target = catalog.find(
            (c) =>
                c.service_type === rule.service_type &&
                (c.repair_type ?? '') === rule.repair_type &&
                (c.part ?? '') === rule.part &&
                (rule.position ? c.position === rule.position : !c.position),
        );
        return target?.id ?? null;
    };

    const submit = async (force: boolean): Promise<void> => {
        if (!pricebook || submitting) return;
        if (!rule.service_type || !rule.repair_type || !rule.part) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Service Type, Repair Type, and Part are required' },
            });
            return;
        }
        if (!requiresQuote && !rule.price) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Price is required (or check "Manual Quote Required")' },
            });
            return;
        }
        const serviceDetailId = findServiceDetailId();
        if (!serviceDetailId) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: {
                    alert: 'Invalid combination of Service Type, Repair Type, Part, and Position',
                },
            });
            return;
        }
        if (yearRangeInvalid) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: {
                    alert: `End year (${endYear}) cannot be less than start year (${startYear})`,
                },
            });
            return;
        }
        const yearRange: YearRange | undefined =
            startYear || endYear
                ? { start: startYear, end: endYear }
                : undefined;
        const body = {
            rules: [
                {
                    service_detail_id: serviceDetailId,
                    price: requiresQuote ? null : parseFloat(rule.price),
                    year_range: yearRange,
                    make: rule.make.length > 0 ? rule.make : undefined,
                    model: rule.model.length > 0 ? rule.model : undefined,
                    includes_future_models: rule.includes_future_models || undefined,
                    special_order: rule.special_order || undefined,
                },
            ],
            force: force || undefined,
        };
        setSubmitting(true);
        try {
            if (editRule) {
                await pricebookApi.updateRule(pricebook.id, editRule.id, body);
            } else {
                await pricebookApi.addRule(pricebook.id, body);
            }
            errorCtx.DISPATCH({
                type: 'NO_ERROR',
                payload: { alert: editRule ? 'Rule updated successfully' : 'Rule added successfully' },
            });
            onAdded();
            onClose();
        } catch (err) {
            if (isConflictError(err)) {
                const data = (err as AxiosLikeError).response!.data as ConflictPayload;
                onConflict(data, () => submit(true));
                return;
            }
            const message =
                (err as AxiosLikeError).response?.data?.message ??
                (err as AxiosLikeError).message ??
                (editRule ? 'Error updating rule' : 'Error adding rule');
            errorCtx.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: message },
            });
        } finally {
            setSubmitting(false);
        }
    };

    const futureModelsDisabled = rule.make.length === 0 || rule.model.length > 0;

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (submitting && reason === 'backdropClick') return;
                onClose();
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{editRule ? 'Edit Rule' : 'Add New Rule'}</DialogTitle>
            <DialogContent>
                {yearRangeInvalid && (
                    <Typography sx={styles.warnText(theme)}>
                        End year cannot be less than start year.
                    </Typography>
                )}
                <Box sx={styles.grid}>
                    <FieldWrapper filled={Boolean(rule.service_type)} required>
                        <TextField
                            fullWidth
                            label="Service Type"
                            value={rule.service_type}
                            disabled
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(rule.repair_type)} required>
                        <Autocomplete<string, false, false, true>
                            freeSolo
                            options={repairTypeOptions}
                            value={rule.repair_type || null}
                            onChange={(_, value) =>
                                setRule({ ...rule, repair_type: (value as string) ?? '' })
                            }
                            label="Repair Type"
                            required
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(rule.part)} required>
                        <Autocomplete<string, false, false, true>
                            freeSolo
                            options={partOptions}
                            value={rule.part || null}
                            onChange={(_, value) =>
                                setRule({ ...rule, part: (value as string) ?? '' })
                            }
                            label="Part"
                            required
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(rule.position)}>
                        <Autocomplete<string, false, false, true>
                            freeSolo
                            options={positionOptions}
                            value={rule.position || null}
                            onChange={(_, value) =>
                                setRule({ ...rule, position: (value as string) ?? '' })
                            }
                            label="Position"
                            helperText="Leave blank if this rule applies regardless of position"
                        />
                    </FieldWrapper>
                    <Box>
                        <FieldWrapper filled={Boolean(rule.price)} required={!requiresQuote}>
                            <CurrencyInput
                                fullWidth
                                label="Price"
                                value={rule.price}
                                onChange={(v) => setRule({ ...rule, price: v })}
                                disabled={requiresQuote}
                            />
                        </FieldWrapper>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={requiresQuote}
                                    onChange={(e) => {
                                        setRequiresQuote(e.target.checked);
                                        if (e.target.checked) setRule({ ...rule, price: '' });
                                    }}
                                />
                            }
                            label="Manual Quote Required"
                        />
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={rule.special_order}
                                    onChange={(e) =>
                                        setRule({ ...rule, special_order: e.target.checked })
                                    }
                                />
                            }
                            label="Special Order"
                        />
                        <Typography sx={styles.helpText}>
                            Indicates this rule requires customer-specific follow-up action.
                        </Typography>
                    </Box>
                    <FieldWrapper filled={Boolean(rule.year_start)}>
                        <TextField
                            fullWidth
                            label="Year Start"
                            type="number"
                            value={rule.year_start}
                            onChange={(e) => setRule({ ...rule, year_start: e.target.value })}
                            helperText={
                                rule.open_ended
                                    ? 'Leave blank for all years; future years included'
                                    : 'Leave blank for all years'
                            }
                        />
                    </FieldWrapper>
                    <Box>
                        <FieldWrapper filled={Boolean(rule.year_end) || rule.open_ended}>
                            <TextField
                                fullWidth
                                label="Year End"
                                type="number"
                                value={rule.open_ended ? '9999' : rule.year_end}
                                onChange={(e) => setRule({ ...rule, year_end: e.target.value })}
                                disabled={rule.open_ended}
                                error={yearRangeInvalid}
                                helperText={
                                    yearRangeInvalid
                                        ? 'End year cannot be less than start year'
                                        : rule.open_ended
                                            ? 'Includes future years'
                                            : 'Set end year or toggle Include Future Years'
                                }
                            />
                        </FieldWrapper>
                        <FormControlLabel
                            sx={styles.checkboxBelowField}
                            control={
                                <Checkbox
                                    checked={rule.open_ended}
                                    onChange={(e) =>
                                        setRule({ ...rule, open_ended: e.target.checked })
                                    }
                                />
                            }
                            label="Include Future Years"
                        />
                    </Box>
                    <FieldWrapper filled={rule.make.length > 0}>
                        <Autocomplete<string, true, false, true>
                            multiple
                            freeSolo
                            options={vehicleMakes}
                            value={rule.make}
                            onChange={(_, value) =>
                                setRule({ ...rule, make: value as string[] })
                            }
                            label="Make"
                            helperText="Leave blank to apply to all makes."
                        />
                    </FieldWrapper>
                    <Box>
                        <FieldWrapper filled={rule.model.length > 0}>
                            <Autocomplete<string, true, false, true>
                                multiple
                                freeSolo
                                options={models}
                                value={rule.model}
                                onChange={(_, value) =>
                                    setRule({ ...rule, model: value as string[] })
                                }
                                label="Model"
                                helperText="Leave blank to apply to all models."
                            />
                        </FieldWrapper>
                        <FormControlLabel
                            sx={styles.checkboxBelowField}
                            control={
                                <Checkbox
                                    checked={rule.includes_future_models}
                                    disabled={futureModelsDisabled}
                                    onChange={(e) =>
                                        setRule({
                                            ...rule,
                                            includes_future_models: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Include Future Models"
                        />
                        <Typography sx={styles.helpText}>
                            Available only when a make is selected and no specific models are chosen.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={yearRangeInvalid}
                    loading={submitting}
                    onClick={() => void submit(false)}
                >
                    {editRule ? 'Save Changes' : 'Add Rule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddRuleDialog;
